/**
 * How the classic interface shows what the engine needs to show
 * (engine/host.js): its notices as toasts, its questions as today's
 * dialogs, its long operations as progress cards, colours from the theme.
 * Nothing changes on screen: these are the calls the engine made itself
 * before Phase 5b, step 2.
 */
import { engine, NOTICE, QUESTION } from "../../../engine/index.js";
import { on, Events } from "../../../engine/core/events.js";
import { showToast } from "./toast.js";
import { showRevocationPrompt, showRefusedChangesPrompt, confirm } from "./modals.js";
import { showMigrationModal, updateMigrationProgress, hideMigrationModal } from "./migration-modal.js";
import { getNewColor } from "./inline-editor.js";
import { withOperation, beginOperation } from "./operation-progress.js";
import { reportAttachmentFailure } from "./attachment-failures.js";

/**
 * Notices shown another way than a toast, by code: handler(notice).
 * @type {Map<string, (notice: Object) => void>}
 */
const presenters = new Map();

/** Show one code's notices another way than the default toast. */
export function presentNotice(code, handler) {
  presenters.set(code, handler);
}

let installed = false;

export function installEngineHost() {
  if (installed) return;
  installed = true;
  engine.setHost({
    async ask(question) {
      if (question.code === QUESTION.RECOVER_LINKED_MAP) return showRevocationPrompt(question.data);
      if (question.code === QUESTION.CONFIRM) return confirm(question.data);
      if (question.code === QUESTION.REFUSED_CHANGES) return showRefusedChangesPrompt(question.data);
      return question.fallback;
    },
    runOperation: (title, work, describeResult) => withOperation(title, work, describeResult),
    beginOperation: (title, options) => beginOperation(title, options),
    beginBlockingOperation(texts) {
      showMigrationModal(texts);
      return {
        // Two frames: the card is on screen before the operation holds the page.
        paint: () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve))),
        update: (current, total) => updateMigrationProgress(current, total),
        finish: (success) => hideMigrationModal(success),
      };
    },
    newItemColor: (usedColors) => getNewColor(usedColors, null),
  });
  presentNotice(NOTICE.ATTACHMENT_FAILED, (notice) => reportAttachmentFailure(notice.data));
  // As before: a dialog that waits, the page stays as it is.
  presentNotice(NOTICE.SAVE_FAILED, (notice) => window.alert(notice.message));
  presentNotice(NOTICE.SIGN_OUT_CHECK_FAILED, (notice) => window.alert(notice.message));
  on(Events.ENGINE_NOTICE, (notice) => {
    const present = presenters.get(notice.code);
    if (present) return present(notice);
    if (notice.message) showToast(notice.message, { type: notice.level, ...(notice.duration ? { duration: notice.duration } : {}) });
  });
}
